import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/** Storefront PLP filters drawer flag (shareable). Only `=open` is written; absent means closed. */
export const FILTERS_QUERY_PARAM = 'filters';
export const FILTERS_OPEN = 'open';
export const FILTERS_CLOSED = 'closed';

/** Storefront PLP sort drawer flag (shareable). Only `=open` is written; absent means closed. */
export const ORDINATION_QUERY_PARAM = 'ordination';
export const ORDINATION_OPEN = 'open';
export const ORDINATION_CLOSED = 'closed';

/** Pending post-login / wishlist flows — not part of listing data; stripped for loader revalidate comparisons. */
export const ACTION_PARAMS = ['action', 'actionParams'] as const;

/**
 * Keyword typed in the Page Designer FAQ search (shareable). The FAQ articles are already loaded by the
 * component loader, so the filtering is client-side and the page loader must not re-run on every keystroke.
 */
export const FAQ_QUERY_PARAM = 'faq';

export function getInitialFiltersOpen(searchParams: URLSearchParams): boolean {
    return searchParams.get(FILTERS_QUERY_PARAM) === FILTERS_OPEN;
}

export function getInitialOrdinationOpen(searchParams: URLSearchParams): boolean {
    return searchParams.get(ORDINATION_QUERY_PARAM) === ORDINATION_OPEN;
}

export function stripQueryParams(search: string, keys: readonly string[]): string {
    const params = new URLSearchParams(search);
    for (const key of keys) {
        params.delete(key);
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

export function getSearchWithoutFiltersParam(search: string): string {
    return stripQueryParams(search, [FILTERS_QUERY_PARAM]);
}

export function getSearchWithoutOrdinationParam(search: string): string {
    return stripQueryParams(search, [ORDINATION_QUERY_PARAM]);
}

/** Strips PLP drawer UI params while preserving search / refine / sort. */
export function getSearchWithoutPlpPanelUiParams(search: string): string {
    return stripQueryParams(search, [FILTERS_QUERY_PARAM, ORDINATION_QUERY_PARAM]);
}

export function getSearchWithoutActionParams(search: string): string {
    return stripQueryParams(search, ACTION_PARAMS);
}

/** Strips client-only URL state for `shouldRevalidate` comparisons. */
export function getSearchWithoutClientOnlyParams(search: string): string {
    return stripQueryParams(search, [FILTERS_QUERY_PARAM, ORDINATION_QUERY_PARAM, FAQ_QUERY_PARAM, ...ACTION_PARAMS]);
}

type ListingNavigationLocation = {
    pathname: string;
    search: string;
};

/**
 * True when an in-flight navigation on the same pathname changes listing data params
 * (refine, sort, page, …) but not client-only UI params (filters, ordination, action).
 */
export function isSamePathnameListingDataNavigationPending(args: {
    pathname: string;
    search: string;
    navigationState: 'idle' | 'loading' | 'submitting';
    navigationLocation: ListingNavigationLocation | undefined;
}): boolean {
    const { pathname, search, navigationState, navigationLocation } = args;

    if (navigationState === 'idle' || !navigationLocation) {
        return false;
    }

    if (navigationLocation.pathname !== pathname) {
        return false;
    }

    return getSearchWithoutClientOnlyParams(search) !== getSearchWithoutClientOnlyParams(navigationLocation.search);
}

/**
 * Next search string when toggling the filters drawer. Opening filters closes the ordination drawer in the URL.
 * When closed, the `filters` key is removed (no `filters=closed`).
 */
export function buildSearchAfterFiltersToggle(search: string, nextFiltersOpen: boolean): string {
    const params = new URLSearchParams(search);
    if (nextFiltersOpen) {
        params.set(FILTERS_QUERY_PARAM, FILTERS_OPEN);
        params.delete(ORDINATION_QUERY_PARAM);
    } else {
        params.delete(FILTERS_QUERY_PARAM);
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

/**
 * Next search string when toggling the ordination drawer. Opening ordination closes the filters drawer in the URL.
 * When closed, the `ordination` key is removed (no `ordination=closed`).
 */
export function buildSearchAfterOrdinationToggle(search: string, nextOrdinationOpen: boolean): string {
    const params = new URLSearchParams(search);
    if (nextOrdinationOpen) {
        params.set(ORDINATION_QUERY_PARAM, ORDINATION_OPEN);
        params.delete(FILTERS_QUERY_PARAM);
    } else {
        params.delete(ORDINATION_QUERY_PARAM);
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

/**
 * Default product-search sort applied by `fetchSearchProducts` when the URL has no `sort` param.
 * Not always present in BM `sortingOptions`; the UI prepends it as "Relevance".
 */
export const BEST_MATCHES_SORT_ID = 'best-matches';

/**
 * Applies sort, resets pagination for the storefront `page` param, and removes the ordination drawer flag.
 */
export function applySortAndCloseOrdinationPanel(search: string, sortId: string): string {
    const params = new URLSearchParams(search);
    params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sortId);
    params.delete(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
    params.delete(ORDINATION_QUERY_PARAM);
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

/** Clears sort so the loader/search client apply the engine default (`best-matches`). */
export function applyDefaultSortAndCloseOrdinationPanel(search: string): string {
    const params = new URLSearchParams(search);
    params.delete(PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    params.delete(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
    params.delete(ORDINATION_QUERY_PARAM);
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}
